import { createContext, useContext } from 'react';
import type {
  NewOrderNotificationPayload,
  NotificationHubHandle,
} from '@kss/api';

/**
 * In-memory record of a notification observed in this session. Kept
 * so a widget on the dashboard can render "recent notifications" —
 * SignalR events themselves are fire-and-forget, so if we don't
 * remember them here they're gone.
 */
export interface StoredNotification {
  /** Stable id derived from the payload (or a random fallback). */
  id: string;
  /** ISO timestamp — either from the payload's `orderDate` or receipt time. */
  receivedAt: string;
  payload: NewOrderNotificationPayload;
}

export interface NotificationsContextValue {
  /**
   * The last N notifications received in this browser tab. Newest first.
   * Empty until an event actually arrives.
   */
  recent: StoredNotification[];
  /** Manually clear the in-memory history (used by the dashboard widget). */
  clear: () => void;
  /**
   * Live SignalR handle, or null when the hub isn't connected (user
   * logged out, hub unreachable, etc.). Components subscribing to
   * events directly should always null-check before calling `.on`.
   */
  hub: NotificationHubHandle | null;
}

export const NotificationsContext = createContext<NotificationsContextValue>({
  recent: [],
  clear: () => {},
  hub: null,
});

export function useNotifications(): NotificationsContextValue {
  return useContext(NotificationsContext);
}
